import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Phone, Mail, Calendar as CalendarIcon, StickyNote, CheckSquare,
  TrendingUp, Target, Building2, Users, Trash2, Edit, Link2, X,
  MessageSquare, Briefcase, DollarSign, Download, Upload, Database,
  FileSpreadsheet, FileJson, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEAD_STATUSES = [
  { value: 'new', label: 'New', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  { value: 'contacted', label: 'Contacted', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'qualified', label: 'Qualified', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  { value: 'proposal', label: 'Proposal', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'negotiation', label: 'Negotiation', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'won', label: 'Won', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { value: 'lost', label: 'Lost', color: 'bg-rose-100 text-rose-700 border-rose-200' },
  { value: 'converted_to_tenant', label: 'Converted → Tenant', color: 'bg-violet-100 text-violet-700 border-violet-200' },
];
const ACTIVITY_ICONS = { call: Phone, email: Mail, meeting: CalendarIcon, note: StickyNote, task: CheckSquare };
const PRIORITY_COLORS = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-rose-100 text-rose-700',
};

const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken') || localStorage.getItem('token')}` });

export default function SuperAdminCRM() {
  const [tab, setTab] = useState('pipeline');
  const [leads, setLeads] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadDialog, setShowLeadDialog] = useState(false);
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkLead, setLinkLead] = useState(null);

  const loadAll = async () => {
    try {
      const [l, c, s, t] = await Promise.all([
        axios.get(`${API}/super-admin/crm/leads`, { headers: getHeaders() }),
        axios.get(`${API}/super-admin/crm/companies`, { headers: getHeaders() }),
        axios.get(`${API}/super-admin/crm/stats`, { headers: getHeaders() }),
        axios.get(`${API}/super-admin/crm/tenants-lookup`, { headers: getHeaders() }),
      ]);
      setLeads(l.data); setCompanies(c.data); setStats(s.data); setTenants(t.data);
    } catch {
      toast.error('Failed to load CRM data');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const openNewLead = () => { setEditingLead({}); setShowLeadDialog(true); };
  const openEditLead = (l) => { setEditingLead(l); setShowLeadDialog(true); };
  const openNewCompany = () => { setEditingCompany({}); setShowCompanyDialog(true); };
  const openEditCompany = (c) => { setEditingCompany(c); setShowCompanyDialog(true); };

  const saveLead = async (form) => {
    try {
      if (editingLead?.id) {
        await axios.put(`${API}/super-admin/crm/leads/${editingLead.id}`, form, { headers: getHeaders() });
        toast.success('Lead updated');
      } else {
        await axios.post(`${API}/super-admin/crm/leads`, form, { headers: getHeaders() });
        toast.success('Lead created');
      }
      setShowLeadDialog(false); setEditingLead(null);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const deleteLead = async (l) => {
    if (!window.confirm(`Delete lead "${l.name}"?`)) return;
    try {
      await axios.delete(`${API}/super-admin/crm/leads/${l.id}`, { headers: getHeaders() });
      toast.success('Lead deleted');
      loadAll();
    } catch { toast.error('Delete failed'); }
  };

  const saveCompany = async (form) => {
    try {
      if (editingCompany?.id) {
        await axios.put(`${API}/super-admin/crm/companies/${editingCompany.id}`, form, { headers: getHeaders() });
        toast.success('Company updated');
      } else {
        await axios.post(`${API}/super-admin/crm/companies`, form, { headers: getHeaders() });
        toast.success('Company created');
      }
      setShowCompanyDialog(false); setEditingCompany(null);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const deleteCompany = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try {
      await axios.delete(`${API}/super-admin/crm/companies/${c.id}`, { headers: getHeaders() });
      toast.success('Company deleted');
      loadAll();
    } catch { toast.error('Delete failed'); }
  };

  const linkToTenant = async (leadId, tenantId) => {
    try {
      await axios.post(`${API}/super-admin/crm/leads/${leadId}/link-tenant?tenant_id=${tenantId}`, {}, { headers: getHeaders() });
      toast.success('Lead linked to tenant');
      setLinkLead(null);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Link failed'); }
  };

  if (loading) return <div className="p-8 text-slate-500" data-testid="sa-crm-loading">Loading CRM…</div>;

  return (
    <div className="p-6 space-y-6" data-testid="sa-crm-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Sales CRM</h1>
          <p className="text-slate-500 mt-1">Track prospects, accounts and convert leads into paying tenants.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openNewCompany} data-testid="sa-new-company">
            <Building2 className="w-4 h-4 mr-2" /> New Account
          </Button>
          <Button onClick={openNewLead} data-testid="sa-new-lead">
            <Plus className="w-4 h-4 mr-2" /> New Lead
          </Button>
        </div>
      </div>

      <StatsRow stats={stats} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pipeline" data-testid="sa-tab-pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="leads" data-testid="sa-tab-leads">Leads</TabsTrigger>
          <TabsTrigger value="companies" data-testid="sa-tab-companies">Accounts</TabsTrigger>
          <TabsTrigger value="data" data-testid="sa-tab-data">
            <Database className="w-3.5 h-3.5 mr-1.5" />Import / Export
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pipeline" className="mt-6">
          <PipelineBoard leads={leads} onOpen={(l) => setSelectedLead(l)} />
        </TabsContent>
        <TabsContent value="leads" className="mt-6">
          <LeadsTable leads={leads} onOpen={setSelectedLead} onEdit={openEditLead}
                      onDelete={deleteLead} onLink={(l) => setLinkLead(l)} />
        </TabsContent>
        <TabsContent value="companies" className="mt-6">
          <CompaniesTable companies={companies} onEdit={openEditCompany} onDelete={deleteCompany} />
        </TabsContent>
        <TabsContent value="data" className="mt-6">
          <SADataPanel />
        </TabsContent>
      </Tabs>

      {showLeadDialog && (
        <LeadDialog lead={editingLead} companies={companies} onSave={saveLead}
                    onClose={() => { setShowLeadDialog(false); setEditingLead(null); }} />
      )}
      {showCompanyDialog && (
        <CompanyDialog company={editingCompany} onSave={saveCompany}
                       onClose={() => { setShowCompanyDialog(false); setEditingCompany(null); }} />
      )}
      {selectedLead && (
        <LeadDetailDrawer
          leadId={selectedLead.id}
          onClose={() => setSelectedLead(null)}
          onChanged={loadAll}
          onEdit={(l) => { setSelectedLead(null); openEditLead(l); }}
          onLink={(l) => { setSelectedLead(null); setLinkLead(l); }}
        />
      )}
      {linkLead && (
        <LinkTenantDialog lead={linkLead} tenants={tenants}
                          onClose={() => setLinkLead(null)}
                          onConfirm={(tid) => linkToTenant(linkLead.id, tid)} />
      )}
    </div>
  );
}

function StatsRow({ stats }) {
  if (!stats) return null;
  const cards = [
    { label: 'Total Leads', value: stats.total_leads, icon: Users, color: 'from-indigo-500 to-blue-600' },
    { label: 'Pipeline Value', value: formatMoney(stats.pipeline_value), icon: TrendingUp, color: 'from-amber-500 to-orange-600' },
    { label: 'Converted Tenants', value: stats.converted_to_tenants, icon: Target, color: 'from-emerald-500 to-teal-600' },
    { label: 'Accounts', value: stats.total_companies, icon: Building2, color: 'from-violet-500 to-purple-600' },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map(c => {
        const Icon = c.icon;
        return (
          <Card key={c.label} className="border-slate-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-500 font-medium uppercase tracking-wide">{c.label}</div>
                  <div className="text-2xl font-bold text-slate-900 mt-1">{c.value}</div>
                </div>
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${c.color} flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function PipelineBoard({ leads, onOpen }) {
  const visible = LEAD_STATUSES.filter(s => !['lost', 'converted_to_tenant'].includes(s.value));
  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {visible.map(st => {
        const col = leads.filter(l => l.status === st.value);
        const sum = col.reduce((a, l) => a + (l.value || 0), 0);
        return (
          <div key={st.value} className="min-w-[280px] w-[280px] bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-3">
              <Badge className={`${st.color} border`}>{st.label}</Badge>
              <div className="text-xs text-slate-500">{col.length} · {formatMoney(sum)}</div>
            </div>
            <div className="space-y-2">
              {col.map(l => (
                <button key={l.id} onClick={() => onOpen(l)}
                        className="w-full text-left bg-white hover:shadow-md transition rounded-lg p-3 border border-slate-200">
                  <div className="font-medium text-slate-900 text-sm truncate">{l.name}</div>
                  {l.company_name && <div className="text-xs text-slate-500 truncate">{l.company_name}</div>}
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs font-semibold text-slate-700">{formatMoney(l.value, l.currency)}</span>
                    {l.plan_interested && <Badge variant="outline" className="text-[10px]">{l.plan_interested}</Badge>}
                  </div>
                </button>
              ))}
              {col.length === 0 && <div className="text-center text-xs text-slate-400 py-6">No leads</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LeadsTable({ leads, onOpen, onEdit, onDelete, onLink }) {
  if (leads.length === 0) return <EmptyState label="No leads yet. Add your first prospect." />;
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 text-left">
          <tr>
            <th className="p-3">Name</th><th className="p-3">Company</th><th className="p-3">Plan</th>
            <th className="p-3">Status</th><th className="p-3">Value</th><th className="p-3">Owner</th>
            <th className="p-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {leads.map(l => {
            const st = LEAD_STATUSES.find(s => s.value === l.status);
            return (
              <tr key={l.id} className="hover:bg-slate-50">
                <td className="p-3">
                  <button className="text-indigo-600 hover:underline font-medium" onClick={() => onOpen(l)}>{l.name}</button>
                  {l.email && <div className="text-xs text-slate-500">{l.email}</div>}
                </td>
                <td className="p-3 text-slate-700">{l.company_name || '—'}</td>
                <td className="p-3 text-slate-600">{l.plan_interested || '—'}</td>
                <td className="p-3">{st && <Badge className={`${st.color} border`}>{st.label}</Badge>}</td>
                <td className="p-3 font-medium">{formatMoney(l.value, l.currency)}</td>
                <td className="p-3 text-slate-500 text-xs">{l.assigned_to_name || '—'}</td>
                <td className="p-3 text-right">
                  <div className="flex justify-end gap-1">
                    {l.status !== 'converted_to_tenant' && (
                      <Button size="sm" variant="ghost" onClick={() => onLink(l)} title="Link to Tenant">
                        <Link2 className="w-4 h-4 text-emerald-600" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => onEdit(l)}><Edit className="w-4 h-4 text-slate-500" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => onDelete(l)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CompaniesTable({ companies, onEdit, onDelete }) {
  if (companies.length === 0) return <EmptyState label="No accounts yet." />;
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 text-left">
          <tr>
            <th className="p-3">Company</th><th className="p-3">Industry</th><th className="p-3">Website</th>
            <th className="p-3">Contact</th><th className="p-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {companies.map(c => (
            <tr key={c.id} className="hover:bg-slate-50">
              <td className="p-3 font-medium">{c.name}</td>
              <td className="p-3 text-slate-600">{c.industry || '—'}</td>
              <td className="p-3 text-indigo-600">{c.website ? <a href={c.website} target="_blank" rel="noreferrer">{c.website}</a> : '—'}</td>
              <td className="p-3 text-xs text-slate-500">{c.contact_person}<br />{c.contact_email || c.contact_phone}</td>
              <td className="p-3 text-right">
                <Button size="sm" variant="ghost" onClick={() => onEdit(c)}><Edit className="w-4 h-4 text-slate-500" /></Button>
                <Button size="sm" variant="ghost" onClick={() => onDelete(c)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LeadDialog({ lead, companies, onSave, onClose }) {
  const [form, setForm] = useState({
    name: lead?.name || '', email: lead?.email || '', phone: lead?.phone || '',
    company_id: lead?.company_id || '', company_name: lead?.company_name || '',
    title: lead?.title || '', status: lead?.status || 'new', priority: lead?.priority || 'medium',
    value: lead?.value ?? 0, currency: lead?.currency || 'USD', source: lead?.source || '',
    plan_interested: lead?.plan_interested || '', notes: lead?.notes || '',
  });
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="sa-lead-dialog">
        <DialogHeader><DialogTitle>{lead?.id ? 'Edit Lead' : 'New Lead'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Name *"><Input value={form.name} onChange={e => update('name', e.target.value)} data-testid="sa-lead-name" /></Field>
          <Field label="Title"><Input value={form.title} onChange={e => update('title', e.target.value)} /></Field>
          <Field label="Email"><Input value={form.email} onChange={e => update('email', e.target.value)} /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={e => update('phone', e.target.value)} /></Field>
          <Field label="Account">
            <Select value={form.company_id || '__none'} onValueChange={v => {
              if (v === '__none') { update('company_id', ''); return; }
              update('company_id', v);
              const c = companies.find(x => x.id === v);
              if (c) update('company_name', c.name);
            }}>
              <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">None</SelectItem>
                {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Company Name (free-text)">
            <Input value={form.company_name} onChange={e => update('company_name', e.target.value)} />
          </Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={v => update('status', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {LEAD_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Priority">
            <Select value={form.priority} onValueChange={v => update('priority', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Plan Interested">
            <Select value={form.plan_interested || '__none'} onValueChange={v => update('plan_interested', v === '__none' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">None</SelectItem>
                <SelectItem value="launch">Launch</SelectItem>
                <SelectItem value="growth">Growth</SelectItem>
                <SelectItem value="scale">Scale</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Deal Value"><Input type="number" value={form.value} onChange={e => update('value', e.target.value)} /></Field>
          <Field label="Currency"><Input value={form.currency} onChange={e => update('currency', e.target.value)} /></Field>
          <Field label="Source" full><Input value={form.source} onChange={e => update('source', e.target.value)} /></Field>
          <Field label="Notes" full><Textarea value={form.notes} onChange={e => update('notes', e.target.value)} rows={3} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => {
            if (!form.name.trim()) { toast.error('Name required'); return; }
            onSave(form);
          }} data-testid="sa-save-lead">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompanyDialog({ company, onSave, onClose }) {
  const [form, setForm] = useState({
    name: company?.name || '', industry: company?.industry || '', website: company?.website || '',
    size: company?.size || '', address: company?.address || '', contact_person: company?.contact_person || '',
    contact_email: company?.contact_email || '', contact_phone: company?.contact_phone || '', notes: company?.notes || '',
  });
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="sa-company-dialog">
        <DialogHeader><DialogTitle>{company?.id ? 'Edit Account' : 'New Account'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Name *"><Input value={form.name} onChange={e => update('name', e.target.value)} /></Field>
          <Field label="Industry"><Input value={form.industry} onChange={e => update('industry', e.target.value)} /></Field>
          <Field label="Website"><Input value={form.website} onChange={e => update('website', e.target.value)} /></Field>
          <Field label="Size"><Input value={form.size} onChange={e => update('size', e.target.value)} /></Field>
          <Field label="Contact Person"><Input value={form.contact_person} onChange={e => update('contact_person', e.target.value)} /></Field>
          <Field label="Contact Email"><Input value={form.contact_email} onChange={e => update('contact_email', e.target.value)} /></Field>
          <Field label="Contact Phone"><Input value={form.contact_phone} onChange={e => update('contact_phone', e.target.value)} /></Field>
          <Field label="Address"><Input value={form.address} onChange={e => update('address', e.target.value)} /></Field>
          <Field label="Notes" full><Textarea value={form.notes} onChange={e => update('notes', e.target.value)} rows={3} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => {
            if (!form.name.trim()) { toast.error('Name required'); return; }
            onSave(form);
          }}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LinkTenantDialog({ lead, tenants, onClose, onConfirm }) {
  const [tid, setTid] = useState('');
  const [search, setSearch] = useState('');
  const filtered = tenants.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) || t.slug.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="link-tenant-dialog">
        <DialogHeader><DialogTitle>Link "{lead.name}" to Tenant</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-600">Select the tenant this lead converted into. The lead will be marked as <strong>Converted → Tenant</strong>.</p>
          <Input placeholder="Search tenants…" value={search} onChange={e => setSearch(e.target.value)} />
          <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
            {filtered.map(t => (
              <button
                key={t.id}
                onClick={() => setTid(t.id)}
                className={`w-full text-left p-3 hover:bg-slate-50 ${tid === t.id ? 'bg-indigo-50' : ''}`}
              >
                <div className="font-medium text-slate-900">{t.name}</div>
                <div className="text-xs text-slate-500">{t.slug} · {t.plan} · {t.status}</div>
              </button>
            ))}
            {filtered.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No tenants match</div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!tid} onClick={() => onConfirm(tid)} data-testid="confirm-link-tenant">Link</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeadDetailDrawer({ leadId, onClose, onChanged, onEdit, onLink }) {
  const [lead, setLead] = useState(null);
  const [activityForm, setActivityForm] = useState({ type: 'note', subject: '', description: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/super-admin/crm/leads/${leadId}`, { headers: getHeaders() });
      setLead(res.data);
    } catch { toast.error('Failed to load lead'); onClose(); }
  };
  useEffect(() => { load(); }, [leadId]); // eslint-disable-line

  const addActivity = async () => {
    if (!activityForm.subject.trim()) { toast.error('Subject required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/super-admin/crm/activities`, { ...activityForm, lead_id: leadId }, { headers: getHeaders() });
      setActivityForm({ type: 'note', subject: '', description: '' });
      await load(); onChanged?.();
      toast.success('Activity added');
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const deleteActivity = async (a) => {
    if (!window.confirm('Delete?')) return;
    try {
      await axios.delete(`${API}/super-admin/crm/activities/${a.id}`, { headers: getHeaders() });
      await load(); onChanged?.();
    } catch { toast.error('Delete failed'); }
  };

  if (!lead) return null;
  const st = LEAD_STATUSES.find(s => s.value === lead.status);
  return (
    <div className="fixed inset-0 z-40 flex">
      <button className="flex-1 bg-slate-900/40" onClick={onClose} aria-label="Close" />
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-5 flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500">Lead</div>
            <div className="text-xl font-bold text-slate-900">{lead.name}</div>
            {lead.company_name && <div className="text-sm text-slate-600">{lead.company_name}</div>}
          </div>
          <div className="flex items-center gap-2">
            {st && <Badge className={`${st.color} border`}>{st.label}</Badge>}
            {lead.status !== 'converted_to_tenant' && (
              <Button size="sm" variant="outline" onClick={() => onLink(lead)}>
                <Link2 className="w-4 h-4 mr-1" /> Link Tenant
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => onEdit(lead)}><Edit className="w-4 h-4 mr-1" /> Edit</Button>
            <Button size="icon" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info icon={Mail} label="Email" value={lead.email || '—'} />
            <Info icon={Phone} label="Phone" value={lead.phone || '—'} />
            <Info icon={Briefcase} label="Plan" value={lead.plan_interested || '—'} />
            <Info icon={DollarSign} label="Value" value={formatMoney(lead.value, lead.currency)} />
            <Info icon={Target} label="Source" value={lead.source || '—'} />
            <Info icon={Users} label="Owner" value={lead.assigned_to_name || '—'} />
          </div>
          {lead.notes && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-700 whitespace-pre-wrap">{lead.notes}</div>
          )}
          {lead.converted_to_tenant_id && (
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-sm text-violet-800">
              ✓ Linked to tenant <strong>{lead.converted_to_tenant_slug}</strong> on {new Date(lead.converted_at).toLocaleDateString()}
            </div>
          )}

          <div className="border-t border-slate-200 pt-4">
            <div className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1">
              <MessageSquare className="w-4 h-4" /> Add Activity
            </div>
            <div className="space-y-2">
              <div className="flex gap-2">
                <Select value={activityForm.type} onValueChange={v => setActivityForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(ACTIVITY_ICONS).map(t => (
                      <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input placeholder="Subject" value={activityForm.subject}
                       onChange={e => setActivityForm(f => ({ ...f, subject: e.target.value }))} />
              </div>
              <Textarea placeholder="Description (optional)" value={activityForm.description}
                        onChange={e => setActivityForm(f => ({ ...f, description: e.target.value }))} rows={2} />
              <Button size="sm" onClick={addActivity} disabled={saving}>
                <Plus className="w-4 h-4 mr-1" /> Log Activity
              </Button>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <div className="text-sm font-semibold text-slate-700 mb-2">Activity Timeline</div>
            {(lead.activities || []).length === 0 && <div className="text-xs text-slate-400 py-4 text-center">No activities.</div>}
            <div className="space-y-2">
              {(lead.activities || []).map(a => {
                const Icon = ACTIVITY_ICONS[a.type] || StickyNote;
                return (
                  <div key={a.id} className="flex gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-indigo-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-slate-900">{a.subject}</div>
                        <button onClick={() => deleteActivity(a)} className="text-slate-400 hover:text-rose-500">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {a.description && <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{a.description}</div>}
                      <div className="text-[11px] text-slate-400 mt-1">
                        {a.created_by_name} · {new Date(a.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <Label className="text-xs text-slate-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-slate-400" />
      <div>
        <div className="text-[11px] text-slate-500 uppercase">{label}</div>
        <div className="text-slate-800">{value}</div>
      </div>
    </div>
  );
}

function EmptyState({ label }) {
  return <div className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center text-slate-500">{label}</div>;
}

function formatMoney(v, ccy = 'USD') {
  const num = Number(v || 0);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: ccy, maximumFractionDigits: 0 }).format(num);
  } catch {
    return `${ccy} ${num.toLocaleString()}`;
  }
}

// ============ Super Admin Data Panel ============

function getSAHeaders() {
  const token = localStorage.getItem('superAdminToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function SADataPanel() {
  const [entity, setEntity] = useState('leads');
  const [format, setFormat] = useState('xlsx');
  const [downloading, setDownloading] = useState(false);
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterPriority, setFilterPriority] = useState([]);
  const [filterSource, setFilterSource] = useState('');
  const [filterQuery, setFilterQuery] = useState('');
  const [importEntity, setImportEntity] = useState('leads');
  const [file, setFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [mapping, setMapping] = useState({});
  const [analyzing, setAnalyzing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const STATUSES = ['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'won', 'lost'];
  const PRIORITIES = ['low', 'medium', 'high'];
  const FIELDS_LEADS = [
    { value: '', label: '— Do not import —' }, { value: 'name', label: 'Name (required)' },
    { value: 'email', label: 'Email' }, { value: 'phone', label: 'Phone' },
    { value: 'company_name', label: 'Company' }, { value: 'title', label: 'Title' },
    { value: 'status', label: 'Status' }, { value: 'priority', label: 'Priority' },
    { value: 'value', label: 'Deal Value' }, { value: 'currency', label: 'Currency' },
    { value: 'source', label: 'Source' }, { value: 'notes', label: 'Notes' },
  ];
  const FIELDS_COMPANIES = [
    { value: '', label: '— Do not import —' }, { value: 'name', label: 'Name (required)' },
    { value: 'industry', label: 'Industry' }, { value: 'website', label: 'Website' },
    { value: 'size', label: 'Size' }, { value: 'address', label: 'Address' },
    { value: 'contact_person', label: 'Contact Person' }, { value: 'contact_email', label: 'Contact Email' },
    { value: 'contact_phone', label: 'Contact Phone' }, { value: 'notes', label: 'Notes' },
  ];
  const fieldOptions = importEntity === 'leads' ? FIELDS_LEADS : FIELDS_COMPANIES;
  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);

  const downloadExport = async () => {
    setDownloading(true);
    try {
      const params = { entity, format };
      if (entity === 'leads') {
        if (filterStatus.length) params.status = filterStatus.join(',');
        if (filterPriority.length) params.priority = filterPriority.join(',');
        if (filterSource.trim()) params.source = filterSource.trim();
      }
      if (filterQuery.trim()) params.q = filterQuery.trim();
      const res = await axios.get(`${API}/super-admin/crm/export`, { params, headers: getSAHeaders(), responseType: 'blob' });
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob); const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `sa-crm-${entity}-${stamp}.${format}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${entity} as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    } finally { setDownloading(false); }
  };

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f); setResult(null); setAnalyzing(true);
    try {
      const fd = new FormData(); fd.append('entity', importEntity); fd.append('file', f);
      const res = await axios.post(`${API}/super-admin/crm/import/analyze`, fd, { headers: { ...getSAHeaders(), 'Content-Type': 'multipart/form-data' } });
      setAnalysis(res.data); setMapping(res.data.suggested_mapping || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not analyze file'); setAnalysis(null);
    } finally { setAnalyzing(false); }
  };

  const commitImport = async () => {
    if (!file || !analysis) return;
    if (!Object.values(mapping).includes('name')) { toast.error('One column must be mapped to Name'); return; }
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append('entity', importEntity); fd.append('mapping_json', JSON.stringify(mapping)); fd.append('file', file);
      const res = await axios.post(`${API}/super-admin/crm/import/commit`, fd, { headers: { ...getSAHeaders(), 'Content-Type': 'multipart/form-data' } });
      setResult(res.data); toast.success(`Imported ${res.data.created} row(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally { setCommitting(false); }
  };

  const resetImport = () => { setFile(null); setAnalysis(null); setMapping({}); setResult(null); };

  return (
    <div className="space-y-6" data-testid="sa-crm-data-panel">
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0"><Download className="w-5 h-5 text-emerald-700" /></div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-900">Export Super Admin CRM Data</h3>
              <p className="text-sm text-slate-500 mt-0.5">Download platform-level CRM data (leads, accounts, activities) as Excel, CSV or JSON.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label className="text-xs text-slate-500">Entity</Label>
              <Select value={entity} onValueChange={setEntity}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="leads">Leads</SelectItem><SelectItem value="companies">Accounts</SelectItem><SelectItem value="activities">Activities</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-slate-500">Format</Label>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="xlsx"><div className="flex items-center gap-2"><FileSpreadsheet className="w-4 h-4 text-emerald-600" /> Excel (.xlsx)</div></SelectItem>
                  <SelectItem value="csv"><div className="flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" /> CSV</div></SelectItem>
                  <SelectItem value="json"><div className="flex items-center gap-2"><FileJson className="w-4 h-4" /> JSON</div></SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end"><Button onClick={downloadExport} disabled={downloading} className="w-full" data-testid="sa-export-btn"><Download className="w-4 h-4 mr-2" />{downloading ? 'Preparing…' : 'Download'}</Button></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
            <div className="sm:col-span-2"><Label className="text-xs text-slate-500">Free-text filter (name / email / company)</Label>
              <Input value={filterQuery} onChange={(e) => setFilterQuery(e.target.value)} placeholder="e.g. Acme, priya@…" />
            </div>
            {entity === 'leads' && (
              <>
                <div><Label className="text-xs text-slate-500">Status</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {STATUSES.map(s => (
                      <button key={s} type="button" onClick={() => toggle(filterStatus, setFilterStatus, s)}
                        className={`px-2.5 py-1 text-xs rounded border capitalize ${filterStatus.includes(s) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400'}`}>{s}</button>
                    ))}
                  </div></div>
                <div><Label className="text-xs text-slate-500">Priority</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {PRIORITIES.map(p => (
                      <button key={p} type="button" onClick={() => toggle(filterPriority, setFilterPriority, p)}
                        className={`px-2.5 py-1 text-xs rounded border capitalize ${filterPriority.includes(p) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400'}`}>{p}</button>
                    ))}
                  </div></div>
                <div className="sm:col-span-2"><Label className="text-xs text-slate-500">Source contains (comma-separated for multiple)</Label>
                  <Input value={filterSource} onChange={(e) => setFilterSource(e.target.value)} placeholder="e.g. LinkedIn, Referral, Website" />
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0"><Upload className="w-5 h-5 text-blue-700" /></div>
            <div className="flex-1"><h3 className="text-lg font-semibold text-slate-900">Import from CSV</h3>
              <p className="text-sm text-slate-500 mt-0.5">Bulk-import leads or accounts into Super Admin CRM. Columns are auto-mapped by name.</p>
            </div>
          </div>
          {!analysis && !result && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label className="text-xs text-slate-500">Entity</Label>
                <Select value={importEntity} onValueChange={(v) => { setImportEntity(v); resetImport(); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="leads">Leads</SelectItem><SelectItem value="companies">Accounts</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2"><Label className="text-xs text-slate-500">CSV file (max 10 MB)</Label>
                <Input type="file" accept=".csv,text/csv" onChange={handleFileChange} disabled={analyzing} />
                {analyzing && <p className="text-xs text-slate-500 mt-1">Analyzing headers…</p>}
              </div>
            </div>
          )}
          {analysis && !result && (
            <div className="space-y-3">
              <div className="flex items-center justify-between border-b pb-2">
                <div className="text-sm"><span className="font-medium">{analysis.total_rows}</span> data rows in <span className="font-medium">{file?.name}</span></div>
                <Button variant="ghost" size="sm" onClick={resetImport}>Change file</Button>
              </div>
              {analysis.missing_required?.length > 0 && (
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>Missing required: {analysis.missing_required.join(', ')}</span>
                </div>
              )}
              <div className="border rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-500 uppercase"><tr>
                    <th className="text-left p-2 w-1/3">CSV Column</th><th className="text-left p-2 w-1/3">Sample Value</th><th className="text-left p-2 w-1/3">Import as</th>
                  </tr></thead>
                  <tbody>
                    {analysis.headers.map(h => (
                      <tr key={h} className="border-t">
                        <td className="p-2 font-medium">{h}</td>
                        <td className="p-2 text-slate-500 truncate max-w-[200px]">{analysis.sample_rows?.[0]?.[h] || '—'}</td>
                        <td className="p-2">
                          <select className="w-full text-sm border rounded px-2 py-1 bg-white"
                            value={mapping[h] || ''} onChange={(e) => setMapping({ ...mapping, [h]: e.target.value || null })}>
                            {fieldOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={resetImport} disabled={committing}>Cancel</Button>
                <Button onClick={commitImport} disabled={committing}><Upload className="w-4 h-4 mr-2" />{committing ? 'Importing…' : `Import ${analysis.total_rows} row(s)`}</Button>
              </div>
            </div>
          )}
          {result && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3">
                <CheckCircle2 className="w-5 h-5" />
                <div className="text-sm"><strong>{result.created}</strong> row(s) imported{result.skipped > 0 && <> · <strong>{result.skipped}</strong> skipped</>}</div>
              </div>
              {result.errors?.length > 0 && (
                <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 max-h-40 overflow-y-auto">
                  <div className="font-semibold mb-1">{result.errors.length} error(s):</div>
                  <ul className="list-disc pl-4 space-y-0.5">{result.errors.map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}</ul>
                </div>
              )}
              <div className="flex justify-end"><Button variant="outline" onClick={resetImport}>Import another file</Button></div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
